/** Легенда апвеллинга: шкала индекса + изобаты + придонная стрелка.
 *
 *  Тот же набор пояснений, что у телеграм-карты (`_draw_colorbar` +
 *  `_draw_legend`), но в вёрстке кабинета. Цвета и штриховки берутся из стилей
 *  слоя (`upwellingLegend.ts`), а не дублируются здесь. */
import { formatNumber, useLang, useT } from '../i18n'
import { paletteGradient } from '../maps/colormaps'
import { useLayerPalette, useLayerUnit } from '../maps/useLayerPalette'
import { ARROW_COLOR } from './arrows'
import { HEAT_TICKS, LOW_INDEX_CUTOFF, SAMPLE_SCALE, isobathLegendRows } from './upwellingLegendData'
import { upwFamilyColor } from '../map/useUpwellingIso'
import { UPW_DASH } from '../map/upwellingIsoLayers'

/** Подложка образцов = тон базовой карты (carto light_all). */
const SAMPLE_BACKDROP = '#eef1f4'

interface LegendProps {
  /** Чем закрашено поле. `upwelling` — замороженный индекс (шкала безразмерная). */
  mechanism?: string
  /** Что наложено линиями. */
  iso?: string[]
  /** Горизонт заливки; `null` — механизм безглубинный. */
  depth?: number | null
}

export function UpwellingLegend({ mechanism = 'upwelling', iso = [], depth = null }: LegendProps) {
  const t = useT()
  const { lang } = useLang()
  const rows = isobathLegendRows(SAMPLE_SCALE)
  const palette = useLayerPalette(mechanism)
  const unit = useLayerUnit(mechanism)
  const legacy = mechanism === 'upwelling'
  return (
    <div className="pointer-events-none min-w-[196px] rounded-xl border border-hull bg-abyss/85 px-3.5 py-3 shadow-console backdrop-blur">
      {/* Легенда называет МЕХАНИЗМ и его единицы. У замороженного индекса она
          безразмерная («низкий/средний/высокий»), и это видимая разница: слой,
          который океанолог назвал неверным, не притворяется измерением. */}
      <div className="mb-2 flex items-baseline gap-1.5">
        <span className="text-xs font-semibold text-foam">{t(`layers.${mechanism}`)}</span>
        {!legacy && unit && (
          <span className="text-[10px] text-faint">{t(`units.${unit}`)}</span>
        )}
      </div>

      <div className="h-2.5 rounded" style={{ background: paletteGradient(palette) }} />
      {legacy ? (
        <div className="mt-1.5 flex justify-between text-[10px] text-mist">
          {HEAT_TICKS.map(tick => (
            <span key={tick.value}>{t(tick.labelKey)}</span>
          ))}
        </div>
      ) : (
        <div className="mt-1.5 flex justify-between text-[10px] text-mist">
          <span>{formatNumber(lang, palette?.stops?.[0] ?? 0)}</span>
          <span>{formatNumber(lang, palette?.stops?.[palette.stops.length - 1] ?? 0)}</span>
        </div>
      )}
      {legacy && (
        /* Почему часть воды пустая: ниже порога пиксель не красится вовсе. */
        <div className="mt-1 text-[10px] text-faint">
          {t('models.upwelling.cutoff', { value: formatNumber(lang, LOW_INDEX_CUTOFF, 2) })}
        </div>
      )}
      {legacy && (
        <div className="mt-1 text-[10px] text-faint">{t('upw.legacyNote')}</div>
      )}
      {/* Что физически происходит и что это значит для рыбы (замечание
          пользователей 07.09.2026). Шкала без этого называет ЧИСЛО и молчит о
          смысле: «+0,3 м/сут» само по себе не говорит ни идти туда, ни не идти.
          Знак назван в тексте отдельно — у знакового поля подъём и опускание
          это разные новости, и текст про одну сторону врал бы на половине карты. */}
      <div className="mt-2 border-t border-hull/60 pt-1.5 text-[10px] leading-snug text-mist">
        {t(`upw.meaning.${mechanism}`)}
      </div>
      {/* Горизонт назван, а не подразумевается: у семьи он свой у каждого поля. */}
      {depth != null && (
        <div className="mt-1 text-[10px] text-faint">
          {t('upw.depthNote', { depth: String(depth) })}
        </div>
      )}
      {/* На нуле вертикальный ток почти нулевой — это физика, а не пропажа
          данных (замер: 99,5 % клеток слабее 1 м/сут). Без оговорки пустая
          карта читалась бы как поломка. */}
      {depth === 0 && !legacy && (
        <div className="mt-1 text-[10px] text-faint">{t('upw.surfaceNote')}</div>
      )}
      {/* Наложенные семейства: colorExpr различает МЕХАНИЗМ, штрих — знак. */}
      {iso.length > 0 && (
        <div className="mt-2 space-y-1 border-t border-hull/60 pt-1.5 text-[11px] text-mist">
          {iso.map(id => (
            <div key={id} className="flex items-center gap-2">
              <svg viewBox="0 0 28 10" className="h-2.5 w-7 shrink-0" aria-hidden>
                <rect width="28" height="10" rx="2" fill={SAMPLE_BACKDROP} />
                <line x1="2" y1="3.5" x2="26" y2="3.5" stroke={upwFamilyColor(id)} strokeWidth="1.4" />
                <line
                  x1="2" y1="7" x2="26" y2="7"
                  stroke={upwFamilyColor(id)} strokeWidth="1.4"
                  strokeDasharray={UPW_DASH.join(' ')}
                  strokeLinecap="round"
                />
              </svg>
              <span>{t(`layers.${id}`)}</span>
            </div>
          ))}
          <div className="text-[10px] text-faint">{t('upw.signHint')}</div>
        </div>
      )}

      {/* Изобаты и придонная стрелка — только у замороженного слоя и его
          гейтированной версии: стрелки другого горизонта поверх чужого поля
          показывали бы направление, которого в расчёте нет. */}
      {!legacy ? null : (
      <>
      {/* Образцы лежат на подложке цвета подложки карты, а не панели: тёмная
          тема съедала бы почти чёрную стрелку, светлая — голубую изобату 50 м.
          Заодно видно ровно тот контраст, что будет на карте. */}
      <div className="mt-2 space-y-1 border-t border-hull/60 pt-1.5 text-[11px] text-mist">
        {rows.map(row => (
          <div key={row.depth} className="flex items-center gap-2">
            <svg viewBox="0 0 28 10" className="h-2.5 w-7 shrink-0" aria-hidden>
              <rect width="28" height="10" rx="2" fill={SAMPLE_BACKDROP} />
              <line
                x1="2"
                y1="5"
                x2="26"
                y2="5"
                stroke={row.color}
                strokeWidth={row.width}
                strokeDasharray={row.dash}
                // Как на карте (`line-cap: round`): именно скруглённые торцы
                // превращают пунктир 50 м в точки, а не в чёрточки.
                strokeLinecap="round"
              />
            </svg>
            <span>{row.label}</span>
          </div>
        ))}
      </div>

      <div className="mt-2 flex items-center gap-2 border-t border-hull/60 pt-1.5 text-[11px] text-mist">
        <svg viewBox="0 0 28 10" className="h-2.5 w-7 shrink-0" aria-hidden>
          <rect width="28" height="10" rx="2" fill={SAMPLE_BACKDROP} />
          <line x1="4" y1="5" x2="19" y2="5" stroke={ARROW_COLOR} strokeWidth="1.6" />
          <path d="M19 2 L25 5 L19 8 Z" fill={ARROW_COLOR} />
        </svg>
        <span>{t('models.upwelling.bottomCurrent')}</span>
      </div>
      {/* Почему стрелок нет в части воды: слабое течение — численный шум модели,
          направление у него бессмысленно (field_store.ARROW_MIN_SPEED_MS). */}
      <div className="mt-1 text-[10px] text-faint">
        {t('models.upwelling.weakArrows', { value: formatNumber(lang, 0.02, 2) })}
      </div>
      </>
      )}
    </div>
  )
}
